import { computed, action, observable } from 'mobx';
import { ProductBaseController } from './ProductBaseController';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { emit, events, on } from 'elc-service-bus';
import { getMobileCarouselSettings } from '../constants/ImageThumbnailCarouselSettings';
import { SECONDS } from '../constants/TimeoutSeconds';
import * as queryParamUtils from '../utils/QueryParams';

export class ProductBriefViewController extends ProductBaseController {
    @observable private isProductQuickViewOpen: boolean = false;

    @action
    public openProductQuickView = () => {
        this.isProductQuickViewOpen = true;
    };

    @action
    public closeProductQuickView = () => {
        this.isProductQuickViewOpen = false;
    };

    @action
    public onQuickViewButtonClick = async (skuId: string, positionIndex: number = 0) => {
        this.openProductQuickView();

        emit(events.QUICK_VIEW_TRIGGERED, {
            skuId: skuId,
            positionIndex: positionIndex
        });
    };

    @computed
    get data() {
        const {
            product,
            selectedSku,
            largeImages,
            productBriefImageCarouselSettings,
            isMobileImageCarouselEnabled,
            isDesktopImageCarouselEnabled,
            isProductQuickViewOpen
        } = this;

        return {
            productUrl: this.selectedProductUrl,
            displayName: product.displayNameDecoded,
            productBadge: product.productBadge,
            selectedSku,
            skuBadge: selectedSku.skuBadge,
            largeImages,
            alternateImage: selectedSku.defaultAlternateMediumImage.src,
            isUpdatedInventorySource: selectedSku.isUpdatedInventorySource,
            product,
            productBriefImageCarouselSettings,
            isMobileImageCarouselEnabled,
            isDesktopImageCarouselEnabled,
            isProductQuickViewOpen
        };
    }

    public navigateToProduct = async (positionIndex: number) => {
        // check for query params and push them into history
        const search = queryParamUtils.getQueryParams();
        if (search.length) {
            window.history.pushState({}, '');
        }

        const { skuId } = this.selectedSku;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            this.selectedProductUrl,
            skuId,
            positionIndex
        );
        await navigateToProductUseCase.execute();
    };

    @computed
    private get productBriefImageCarouselSettings() {
        return this.imageCarouselSettings && this.imageCarouselSettings.productGrid;
    }

    @computed
    private get isMobileImageCarouselEnabled() {
        const mobileImageCarouselSettings =
            this.productBriefImageCarouselSettings &&
            getMobileCarouselSettings(this.productBriefImageCarouselSettings);

        return mobileImageCarouselSettings && mobileImageCarouselSettings.enabled;
    }

    @computed
    private get isDesktopImageCarouselEnabled() {
        return (
            this.productBriefImageCarouselSettings && this.productBriefImageCarouselSettings.enabled
        );
    }

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.closeProductQuickView();
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public favoriteUpdated = () => {
        setTimeout(() => this.closeProductQuickView(), SECONDS.ONE);
    };

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.closeProductQuickView();
    };
}
